import { createBrowserClient } from '@/supabase'
import { calculateTotalExperience } from '@/lib/transformExperienceDate'

export const fetchBookmarkList = async ({
  isProposed,
  experience,
  job,
  specialization,
}: {
  isProposed: boolean
  experience?: [number, number]
  job?: string[]
  specialization?: string[]
}) => {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  // maker_bookmarks에서 현재 사용자의 북마크 목록 가져오기
  // profile_id 기반으로 조회 (is_active 미사용 - API·profile-helpers와 동일)
  const { data: profile } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!profile) {
    return { data: [], error: null }
  }

  const { data: bookmarks, error: bookmarksError } = await supabase
    .from('maker_bookmarks')
    .select('*')
    .eq('profile_id', (profile as any).profile_id)
    .eq('proposal_status', isProposed)

  if (bookmarksError) {
    return { data: null, error: bookmarksError }
  }

  if (!bookmarks || bookmarks.length === 0) {
    return { data: [], error: null }
  }

  // 각 북마크의 maker_profile_id로 accounts 정보 가져오기
  const makerProfileIds = (bookmarks as any[]).map((b: any) => b.maker_profile_id)
  const { data: makers, error: makersError } = await supabase
    .from('accounts')
    .select(`
      *,
      account_work_experiences(*),
      account_license(*)
    `)
    .in('profile_id', makerProfileIds)
    .is('deleted_at', null)

  if (makersError) {
    return { data: null, error: makersError }
  }

  // 북마크와 메이커 정보 결합
  const result = (bookmarks as any[]).map((bookmark: any) => {
    const maker = (makers as any[])?.find((m: any) => m.profile_id === bookmark.maker_profile_id)
    return {
      ...bookmark,
      maker_id: bookmark.maker_profile_id, // 레거시 호환: maker_id = maker_profile_id
      manager_id: bookmark.profile_id, // 레거시 호환: manager_id = profile_id
      maker: maker || null
    }
  }).filter((item: any) => item.maker !== null) // 메이커 정보가 없는 북마크 제외

  // 필터 적용
  let filteredResult = result

  // 경력 필터
  if (experience && experience.length === 2) {
    const [minYears, maxYears] = experience
    filteredResult = filteredResult.filter(item => {
      // 경력 데이터가 없는 경우
      if (!item.maker?.account_work_experiences || item.maker.account_work_experiences.length === 0) {
        return minYears === 0 // 신입 필터만 통과
      }
      
      // 타입 변환: null → undefined
      const experiences = item.maker.account_work_experiences.map((exp: any) => ({
        start_date: exp.start_date,
        end_date: exp.end_date ?? undefined
      }))
      
      const totalExp = calculateTotalExperience(experiences)
      const totalYears = totalExp.years + totalExp.months / 12
      
      // 신입 필터 (minYears === 0): 경력이 1년 미만인 경우도 포함
      if (minYears === 0 && totalYears < 1) {
        return true
      }
      
      // 일반 필터: 총 경력이 범위 내인지 확인
      return totalYears >= minYears && (maxYears === null || totalYears <= maxYears)
    })
  }

  // 주직무 필터
  if (job && job.length > 0) {
    filteredResult = filteredResult.filter(item => {
      if (!item.maker?.main_job) return false
      return job.some(j => item.maker?.main_job?.includes(j))
    })
  }

  // 전문분야 필터
  if (specialization && specialization.length > 0) {
    filteredResult = filteredResult.filter(item => {
      if (!item.maker?.expertise) return false
      return specialization.some(s => item.maker?.expertise?.includes(s))
    })
  }

  return { data: filteredResult, error: null }
}
/**
 * 메이커 북마크 추가 (API 사용)
 * @param makerProfileId - 메이커의 profile_id (accounts.profile_id)
 */
export const bookmarkMaker = async (makerProfileId: string) => {
  const response = await fetch('/api/my/bookmarks/makers', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      maker_profile_id: makerProfileId,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
    
    if (response.status === 401) {
      throw new Error('로그인이 필요합니다.')
    }
    if (response.status === 403) {
      throw new Error('기업 계정만 관심 메이커 기능을 사용할 수 있습니다.')
    }
    if (response.status === 404) {
      throw new Error('프로필을 찾을 수 없습니다.')
    }
    if (response.status === 409) {
      throw new Error('이미 북마크된 메이커입니다.')
    }
    
    throw new Error(errorData.error || '북마크 추가에 실패했습니다.')
  }

  const data = await response.json()
  return data
}

/**
 * 레거시 호환: bookmark 함수 (deprecated)
 * @deprecated bookmarkMaker를 사용하세요
 */
export const bookmark = async (makerId: string) => {
  console.warn('[DEPRECATED] bookmark() 함수는 더 이상 사용되지 않습니다. bookmarkMaker()를 사용하세요.')
  // makerId가 user_id인지 profile_id인지 확인 불가능하므로 에러 처리
  throw new Error('bookmark() 함수는 더 이상 사용되지 않습니다. makerProfileId를 사용하는 bookmarkMaker()를 사용하세요.')
}

/**
 * 메이커 북마크 제거 (API 사용)
 * @param makerProfileId - 메이커의 profile_id (accounts.profile_id)
 */
export const unbookmarkMaker = async (makerProfileId: string) => {
  const response = await fetch(`/api/my/bookmarks/makers?maker_profile_id=${encodeURIComponent(makerProfileId)}`, {
    method: 'DELETE',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
    
    if (response.status === 401) {
      throw new Error('로그인이 필요합니다.')
    }
    if (response.status === 403) {
      throw new Error('기업 계정만 관심 메이커 기능을 사용할 수 있습니다.')
    }
    if (response.status === 404) {
      throw new Error('프로필을 찾을 수 없습니다.')
    }
    
    throw new Error(errorData.error || '북마크 제거에 실패했습니다.')
  }

  const data = await response.json()
  return { data, error: null }
}

/**
 * 레거시 호환: unbookmark 함수 (deprecated)
 * @deprecated unbookmarkMaker를 사용하세요
 */
export const unbookmark = async (makerId: string) => {
  console.warn('[DEPRECATED] unbookmark() 함수는 더 이상 사용되지 않습니다. unbookmarkMaker()를 사용하세요.')
  // makerId가 user_id인지 profile_id인지 확인 불가능하므로 에러 처리
  throw new Error('unbookmark() 함수는 더 이상 사용되지 않습니다. makerProfileId를 사용하는 unbookmarkMaker()를 사용하세요.')
}

/**
 * 메이커 북마크 상태 확인 (API 사용)
 * @param makerProfileId - 메이커의 profile_id (accounts.profile_id)
 */
export const checkMakerBookmarked = async (makerProfileId: string): Promise<boolean> => {
  try {
    const response = await fetch(`/api/my/bookmarks/makers?maker_profile_id=${encodeURIComponent(makerProfileId)}`)

    if (!response.ok) {
      // 401, 403 등은 북마크되지 않은 것으로 간주
      if (response.status === 401 || response.status === 403) {
        return false
      }
      // 404는 북마크 없음
      if (response.status === 404) {
        return false
      }
      // 기타 에러는 false 반환
      return false
    }

    const data = await response.json()
    // 배열이 비어있으면 북마크 없음, 있으면 북마크 있음
    return Array.isArray(data) ? data.length > 0 : false
  } catch (error) {
    console.error('북마크 상태 확인 실패:', error)
    return false
  }
}

/**
 * 레거시 호환: checkBookmarked 함수 (deprecated)
 * @deprecated checkMakerBookmarked를 사용하세요
 */
export const checkBookmarked = async (makerId: string): Promise<boolean> => {
  console.warn('[DEPRECATED] checkBookmarked() 함수는 더 이상 사용되지 않습니다. checkMakerBookmarked()를 사용하세요.')
  return false
}

/**
 * 북마크된 메이커 목록 조회 (레거시 - 서버 API 사용 권장)
 * @deprecated GET /api/my/bookmarks/makers API를 사용하세요
 */
export const fetchBookmarkedMakers = async () => {
  console.warn('[DEPRECATED] fetchBookmarkedMakers()는 더 이상 사용되지 않습니다. GET /api/my/bookmarks/makers API를 사용하세요.')
  
  const supabase = createBrowserClient()
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  // 프로필 확인 (API·profile-helpers와 동일하게 is_active 미사용)
  const { data: profile } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!profile) {
    return []
  }

  const { data: bookmarks, error: bookmarksError } = await supabase
    .from('maker_bookmarks')
    .select('*')
    .eq('profile_id', (profile as any).profile_id)
    .order('created_at', { ascending: false })

  if (bookmarksError) {
    console.error('메이커 즐겨찾기 조회 실패:', bookmarksError)
    throw bookmarksError
  }

  if (!bookmarks || bookmarks.length === 0) {
    return []
  }

  const makerProfileIds = (bookmarks as any[]).map((bookmark) => bookmark.maker_profile_id)
  const { data: makers, error: makersError } = await supabase
    .from('accounts')
    .select(`
      profile_id,
      user_id,
      username,
      main_job,
      expertise,
      account_work_experiences(*)
    `)
    .in('profile_id', makerProfileIds)
    .is('deleted_at', null)

  if (makersError) {
    console.error('메이커 정보 조회 실패:', makersError)
    throw makersError
  }

  return (bookmarks as any[])
    .map((bookmark) => {
      const maker = (makers as any[])?.find(
        (makerData: any) => makerData.profile_id === bookmark.maker_profile_id,
      )

      if (!maker) return null

      return {
        ...bookmark,
        maker: {
          ...maker,
          account_work_experiences: maker.account_work_experiences ?? [],
        },
      }
    })
    .filter(Boolean)
}

export const propose = async () => {}

// 프로젝트 북마크 관련 함수들
export const bookmarkProject = async (counselId: number) => {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  // 프로필 확인 (API·profile-helpers와 동일하게 is_active 미사용)
  const { data: profile } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!profile) {
    throw new Error('프로필을 찾을 수 없습니다.')
  }

  // 북마크 추가
  const { data, error } = await supabase
    .from('project_bookmarks' as any)
    .insert({
      profile_id: (profile as any).profile_id,
      counsel_id: counselId,
    } as any)
    .select()
    .single()

  if (error) {
    // 이미 북마크된 경우 (UNIQUE 제약조건 위반)
    if (error.code === '23505' || error.message?.includes('duplicate')) {
      throw new Error('이미 북마크된 프로젝트입니다.')
    }
    throw error
  }

  return data
}

export const unbookmarkProject = async (counselId: number) => {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  // 프로필 확인 (API·profile-helpers와 동일하게 is_active 미사용)
  const { data: profile } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!profile) {
    throw new Error('프로필을 찾을 수 없습니다.')
  }

  // 북마크 삭제
  const { error } = await supabase
    .from('project_bookmarks' as any)
    .delete()
    .eq('profile_id', (profile as any).profile_id)
    .eq('counsel_id', counselId)

  if (error) throw error
  return { success: true }
}

export const checkProjectBookmarked = async (counselId: number): Promise<boolean> => {
  const supabase = createBrowserClient()
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    return false
  }

  // 프로필 확인 (API·profile-helpers와 동일하게 is_active 미사용)
  const { data: profile } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!profile) {
    return false
  }

  // 북마크 확인
  const { data, error } = await supabase
    .from('project_bookmarks' as any)
    .select('id')
    .eq('profile_id', (profile as any).profile_id)
    .eq('counsel_id', counselId)
    .maybeSingle()

  if (error || !data) {
    return false
  }

  return true
}

export const bookmarkTeam = async (teamId: number) => {
  const supabase = createBrowserClient()
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  const { data: profile } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!profile) {
    throw new Error('프로필을 찾을 수 없습니다.')
  }

  const { data, error } = await supabase
    .from('team_bookmarks' as any)
    .insert({
      profile_id: (profile as any).profile_id,
      team_id: teamId,
    } as any)
    .select()
    .single()

  if (error) {
    if (error.code === '23505' || error.message?.includes('duplicate')) {
      throw new Error('이미 찜한 팀입니다.')
    }
    throw error
  }

  return data
}

export const unbookmarkTeam = async (teamId: number) => {
  const supabase = createBrowserClient()
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('로그인이 필요합니다.')
  }

  const { data: profile } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!profile) {
    throw new Error('프로필을 찾을 수 없습니다.')
  }

  const { error } = await supabase
    .from('team_bookmarks' as any)
    .delete()
    .eq('profile_id', (profile as any).profile_id)
    .eq('team_id', teamId)

  if (error) throw error
  return { success: true }
}

export const getBookmarkedTeamIds = async (): Promise<number[]> => {
  const supabase = createBrowserClient()
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    return []
  }

  const { data: profile } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .maybeSingle()

  if (!profile) {
    return []
  }

  const { data, error } = await supabase
    .from('team_bookmarks' as any)
    .select('team_id')
    .eq('profile_id', (profile as any).profile_id)

  if (error || !data) {
    return []
  }

  return data.map((bookmark: any) => bookmark.team_id)
}

export const fetchBookmarkedTeams = async () => {
  const supabase = createBrowserClient()
  const ids = await getBookmarkedTeamIds()

  if (ids.length === 0) {
    return []
  }

  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .in('id', ids)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('팀 즐겨찾기 조회 실패:', error)
    throw error
  }

  if (!data || data.length === 0) {
    return []
  }

  // 각 팀의 매니저 정보 조회
  const enrichedTeams = await Promise.all(
    data.map(async (team: any) => {
      const { data: managerData } = await supabase
        .from('accounts')
        .select('username, profile_id')
        .eq('profile_id', team.manager_profile_id)
        .maybeSingle()

      const { data: teamMembers } = await supabase
        .from('team_members')
        .select('id')
        .eq('team_id', team.id)
        .eq('status', 'active')

      return {
        ...team,
        manager: managerData || null,
        team_members: teamMembers || [],
      }
    })
  )

  return enrichedTeams
}